import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MailCheck, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Alert } from '../../../components/ui/alert';
import { Spinner } from '../../../components/ui/spinner';
import { fetchMe, resendVerification } from '../../../lib/api';
import { StepScaffold } from '../StepScaffold';

const RESEND_COOLDOWN_S = 30;

/**
 * Onboarding step 1 (staging/prod only). Polls the profile until the user clicks
 * the emailed verification link, then shows a brief confirmation before handing
 * control back so the flow advances to the domain step. Locally this screen never
 * appears — signups are created pre-verified.
 */
export function VerifyStep({
  email,
  onVerified,
  eyebrow = 'Confirm your email',
}: {
  email: string;
  onVerified: () => void;
  eyebrow?: string;
}) {
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, refetchInterval: 4000 });
  const resend = useMutation({ mutationFn: resendVerification });

  // Brief "Email confirmed ✓" state before we advance — closes the loop when the
  // link was clicked in another tab, instead of the screen silently vanishing.
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    if (me.data?.emailVerified && !confirmed) {
      setConfirmed(true);
      const t = setTimeout(onVerified, 900);
      return () => clearTimeout(t);
    }
  }, [me.data?.emailVerified, confirmed, onVerified]);

  // Resend is rate-limited client-side so the button can't be hammered: a short
  // cooldown counts down after each send (replacing the old permanent latch).
  const [cooldown, setCooldown] = useState(0);
  const tick = useRef<ReturnType<typeof setInterval>>();
  useEffect(() => () => clearInterval(tick.current), []);
  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_S);
    clearInterval(tick.current);
    tick.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) clearInterval(tick.current);
        return Math.max(0, c - 1);
      });
    }, 1000);
  };

  const canResend = !resend.isPending && cooldown === 0 && !confirmed;

  if (confirmed) {
    return (
      <StepScaffold
        eyebrow={eyebrow}
        title="Email confirmed"
        lead="Thanks — taking you to the next step."
        footer={<span />}
      >
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-success-dim">
            <CheckCircle2 className="h-8 w-8 text-success" aria-hidden />
          </span>
          <Badge variant="success" dot>
            Email confirmed
          </Badge>
          <Spinner size="sm" />
        </div>
      </StepScaffold>
    );
  }

  return (
    <StepScaffold
      eyebrow={eyebrow}
      title="Check your inbox"
      lead={`We sent a confirmation link to ${email}. Click it and this page moves on automatically — no need to come back here.`}
      safety="Confirming proves you own this address and keeps your workspace secure. Nothing else is needed to continue."
      footer={
        <Button
          type="button"
          variant="outline"
          disabled={!canResend}
          onClick={() => {
            resend.mutate();
            startCooldown();
          }}
        >
          {resend.isPending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend email'}
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-3 py-2 text-center">
        <span className="grid h-16 w-16 place-items-center rounded-full bg-iris-dim">
          <MailCheck className="h-8 w-8 text-iris" aria-hidden />
        </span>
        <Badge variant="neutral" className="gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-iris motion-safe:animate-pulse" />
          Waiting for confirmation
        </Badge>
        <p className="max-w-sm text-xs leading-relaxed text-muted">
          Didn’t arrive in a minute? Check your spam or promotions folder — or resend below.
        </p>
      </div>

      {resend.isError && (
        <Alert variant="danger">
          <span className="font-semibold text-bone">We couldn’t send that email.</span> Wait a
          moment and try Resend again.
        </Alert>
      )}
      {resend.isSuccess && cooldown > 0 && !resend.isError && (
        <Alert variant="success">A fresh link is on its way to {email}.</Alert>
      )}
      {me.isError && (
        <Alert variant="warning">
          Having trouble checking your status — we’ll keep trying automatically.
        </Alert>
      )}
    </StepScaffold>
  );
}
